import type {
  AccuracyArea,
  AccuracyStudy,
  AreaId,
} from './accuracyLedger';

/* Colour for the accuracy track.
 *
 * Same method as components/science/listing/categoryColors.ts: every value is
 * a step from the Ultrahuman Color System, using the two steps named for this
 * job — 50 for a wash and 700 for text on light. Nothing is hand-mixed, and
 * nothing is colour-coded inline anywhere else; every consumer reads here.
 */

export interface Tone {
  bg: string;
  fg: string;
}

/* One dot colour per area, so an area head is recognisable before it is read
   and the nine areas stay distinguishable when "All" shows them in sequence.
   Hues are borrowed from the study categories they correspond to, so an area
   and its category read as the same subject across the two tracks. */
export const AREA_TONE: Record<AreaId, Tone> = {
  activity: { bg: '#E6EEFD', fg: '#1F5DBE' }, // blue — performance
  hr: { bg: '#FBE5ED', fg: '#8E1849' }, // magenta — cardiovascular
  steps: { bg: '#E6EEFD', fg: '#1F5DBE' },
  sleep: { bg: '#E3E8FE', fg: '#0A22AD' }, // indigo — night
  temperature: { bg: '#FFEFE7', fg: '#C44315' }, // orange — thermal
  cycle: { bg: '#E1F7F8', fg: '#017376' }, // teal — women's health
  spo2: { bg: '#E5FBEF', fg: '#1E9A55' }, // green — oxygen
  metabolic: { bg: '#FFEFE7', fg: '#C44315' },
  /* violet — biological age. The only area that had no hue: it sat on neutral
     grey while every other row carried one, so the newest cluster read as the
     unfinished one. Violet is what the wheel had left — blue, indigo, magenta,
     orange, teal and green are all spoken for above, and violet is furthest
     from each of them. Contrast 6.59:1 on its own fill, mid-range for this
     table (the six above run 3.34 to 9.34). */
  longevity: { bg: '#F1E8FD', fg: '#6B25C4' },
};

/* Sensors.
 *
 * The chip is a neutral fill with a coloured dot rather than a coloured fill.
 * Two reasons. The fill is what makes the sensor legible at a glance — it was
 * a hairline outline before and was the quietest thing in the row — while the
 * dot is what tells the three apart. And keeping the hue on a 6px dot leaves
 * the page's blue free to mean one thing in text: an ongoing study. */
export const SENSOR_DOT: Record<string, string> = {
  ppg: '#0279E8', // the science accent, and the colour PPG is drawn in on every chart
  mot: '#C44315',
  temp: '#017376',
};

/* The figure band's fill.
 *
 * Flat, and deliberately so: v3primitives records that dark surfaces are FLAT
 * colour, never gradients, and the band now honours that rule rather than
 * carving an exception out of it.
 *
 * Near-black rather than blue. Blue is already spoken for on this page -- it is
 * the colour the ring's own series is drawn in on every chart, and it is the
 * ongoing-study status -- so a blue band made the page's loudest surface the
 * same hue as two things that carry specific meaning. This carries none, which
 * is what a backdrop wants. */
export const BAND_FILL = '#2A262F';

/* The same three sensors, on the detail page's dark band.
 *
 * On a near-black ground the temperature teal (#017376) and the motion rust
 * (#C44315) both sink into it, and they lose their separation from each other.
 * These are the same hues lifted to read on a deep ground, so the key survives
 * the inversion rather than collapsing into three white dots -- which is what
 * the obvious fix would have done, and it would have thrown away the one thing
 * the dot is there to say. */
export const SENSOR_DOT_ON_DARK: Record<string, string> = {
  ppg: '#66B6FF',
  mot: '#FF9166',
  temp: '#4FD1D4',
};

/* Ongoing study.
 *
 * The page's blue, not an amber warning. An ongoing study is not a caution —
 * it is a study still gathering people, and the earlier amber read as one. */
export const ONGOING = { fg: '#0A5DC2', dot: '#0279E8' };

/* Pure helpers shared by the ledger and its client consumers. They live here
   rather than in accuracyLedger.ts because the ledger imports the 368KB plot
   JSON: any value a client component pulls from there ships that JSON. */

/** Validated: a published figure AND no longer gathering people. An ongoing
    study is still published and readable; it is simply not counted here. */
export const isValidated = (row: {
  status: 'live' | 'soon';
  ongoing: boolean;
}): boolean => row.status === 'live' && !row.ongoing;

/** Every row across the areas with its area and bucket labels — the ledger's
    ALL_ACCURACY_STUDIES minus the plot payload. */
export const flattenAreas = (
  areas: AccuracyArea[],
): Omit<AccuracyStudy, 'activity'>[] =>
  areas.flatMap((a) =>
    a.buckets.flatMap((b) =>
      b.rows.map((r) => ({
        ...r,
        area: a.id,
        areaLabel: a.label,
        bucketLabel: b.label,
      })),
    ),
  );
